import { Fragment, useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { TitleInfo } from "components/TableDetails/components/TitleInfo";
import { renderAvailableData } from "components/TableDetails/functions";
import { useSharedStyles } from "components/TableDetails/hooks";
import AuthContext from "context/auth/authContext";
import TableContext from "context/table/tableContext";

import AccessGroup from "./AccessGroup";
import { GENERAL } from "./constants";
import DateAt from "./DateAt";
import Role from "./Role";
import { CARD_ITEMS, DetailsData } from "./types";

const General = () => {
    const { gray200 } = colors;

    const { isSupportUser } = useContext(AuthContext);
    const { detailsData } = useContext(TableContext);

    const { t } = useTranslation();
    const { sharedStyles } = useSharedStyles();

    const {
        access_groups,
        id,
        roles,
        invited_by,
        created_at,
        updated_at
    }: DetailsData = detailsData;

    const list = [
        {
            title: CARD_ITEMS.ID,
            value: id,
            isAvailable: true
        },
        {
            title: CARD_ITEMS.ROLE,
            isAvailable: roles.length > 0,
            customRender: <Role roles={roles} />
        },
        {
            title: CARD_ITEMS.INVITED_BY,
            value: invited_by?.email,
            isAvailable: invited_by !== null
        },

        {
            title: CARD_ITEMS.ACCESS_GROUP,
            isAvailable: isSupportUser && access_groups.length > 0,
            customRender: <AccessGroup accessGroups={access_groups} />
        },

        {
            title: CARD_ITEMS.CREATED_AT,
            customRender: (
                <DateAt title={CARD_ITEMS.CREATED_AT} date={created_at} />
            ),
            isAvailable: created_at !== null
        },
        {
            title: CARD_ITEMS.UPDATED_AT,
            customRender: (
                <DateAt title={CARD_ITEMS.UPDATED_AT} date={updated_at} />
            ),
            isAvailable: updated_at !== null
        }
    ];

    return (
        <div
            css={css({
                border: `1px solid ${gray200}`,
                borderRadius: "4px",
                margin: "16px 0 24px 0",
                padding: "16px 24px",
                fontSize: "14px",
                letterSpacing: "0.1px",

                ...sharedStyles
            })}
        >
            <div>
                {t(`Table##${GENERAL}`)}

                <TitleInfo
                    aria-label={t(`Table##${GENERAL}`)}
                    cardName={GENERAL}
                    list={list}
                />
            </div>

            <div>
                {renderAvailableData(list).map(
                    ({ title, customRender, value }) => {
                        return (
                            <Fragment key={title}>
                                {customRender || (
                                    <>
                                        <span>{t(`Table##${title}`)}:</span>
                                        <span>{value}</span>
                                    </>
                                )}
                            </Fragment>
                        );
                    }
                )}
            </div>
        </div>
    );
};

export default General;
